// Payroll → Employees tab: the employee master (contact, bank, salary
// components, leave type, active flag, joining date). List / add / edit /
// deactivate / reactivate / delete. Rendered ONLY inside Payroll.js, behind
// the is_payroll_admin gate and the password lock — never mount this
// anywhere else.
//
// Leavers are DEACTIVATED, never deleted: is_active=false is a single-column
// update that payrollCalc reads (excluded from the leave-type gate and the
// missing-from-muster list; blocked with no slip if they still appear in a
// muster). Every payslip and attendance row stays exactly as stored.
//
// Delete exists only for a record added by mistake: the app refuses it when
// any payslip or attendance row references the employee, and the database
// refuses it independently (both FKs are ON DELETE RESTRICT — see
// supabase/manual/2026-09-05_payroll_fk_restrict.sql), so no path can
// destroy payroll history.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';
import { normalizePhone, isValidMobile, uanIssue, grossMismatch } from '../utils/payrollParse';
import { Button, Field, Badge } from './ui';
import { Plus, Search, Loader2, AlertTriangle, CheckCircle2, Pencil, UserX, UserCheck, Trash2 } from 'lucide-react';

const card = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  padding: 20,
};

const inr = (v) => (v === null || v === undefined || v === '' ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const num = { fontVariantNumeric: 'tabular-nums' };
const numRight = { ...num, textAlign: 'right' };

const PAYMENT_TYPES = ['Bank Account', 'Net Banking', 'Cash'];
// leave_type is deliberately nullable with NO default: HR sets it per employee.
// The payroll calculation refuses to run while any ACTIVE employee is unset.
const LEAVE_TYPES = [
  { value: 'L1', label: 'L1 — 4 L1 per month' },
  { value: 'WO', label: 'WO — weekly offs, no L1' },
];
const MONEY_FIELDS = [
  { key: 'gross_monthly',    label: 'Gross monthly' },
  { key: 'basic',            label: 'Basic' },
  { key: 'da',               label: 'DA' },
  { key: 'residual_pay',     label: 'Residual pay' },
  { key: 'pf',               label: 'PF' },
  // The rupee deduction. The employee's ESIC insurance number is the separate
  // esic_number text field — the two must never share a label.
  { key: 'esic',             label: 'ESIC deduction' },
  { key: 'professional_tax', label: 'Professional tax' },
];
// Display order: active employees first, then inactive, each alphabetical by
// name (case/accent-insensitive), with employee_code as a stable tie-break.
const byNameActiveFirst = (a, b) => {
  const aInactive = a.is_active === false ? 1 : 0;
  const bInactive = b.is_active === false ? 1 : 0;
  if (aInactive !== bInactive) return aInactive - bInactive;
  const byName = String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
  if (byName !== 0) return byName;
  return String(a.employee_code || '').localeCompare(String(b.employee_code || ''));
};

const SELECT = 'id, employee_code, name, designation, department, phone, uan, esic_number, payment_type, account_number, ifsc_code, gross_monthly, basic, da, residual_pay, pf, esic, professional_tax, leave_type, is_active, joining_date, updated_at';

const emptyForm = () => ({
  id: null,
  employee_code: '', name: '', designation: '', department: '', phone: '', uan: '', esic_number: '',
  payment_type: 'Bank Account', account_number: '', ifsc_code: '',
  gross_monthly: '', basic: '', da: '', residual_pay: '', pf: '', esic: '', professional_tax: '',
  leave_type: '', is_active: true, joining_date: '',
});

const rowToForm = (r) => ({
  id: r.id,
  employee_code: r.employee_code || '', name: r.name || '', designation: r.designation || '', department: r.department || '',
  phone: r.phone || '', uan: r.uan || '', esic_number: r.esic_number || '',
  payment_type: r.payment_type || '', account_number: r.account_number || '', ifsc_code: r.ifsc_code || '',
  gross_monthly: r.gross_monthly ?? '', basic: r.basic ?? '', da: r.da ?? '', residual_pay: r.residual_pay ?? '',
  pf: r.pf ?? '', esic: r.esic ?? '', professional_tax: r.professional_tax ?? '',
  leave_type: r.leave_type || '', is_active: r.is_active !== false, joining_date: r.joining_date || '',
});

// '' → null, otherwise a rounded number; NaN signals a bad entry
const toMoney = (v) => {
  if (v === null || v === undefined || String(v).trim() === '') return null;
  const n = Number(String(v).replace(/,/g, '').trim());
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : NaN;
};

// Blocking errors + advisory warnings for the form. Warnings never block a
// save (a leaver may legitimately have no phone; a Cash employee no account).
function validate(f) {
  const errors = {};
  const warnings = [];
  const code = f.employee_code.trim();
  if (!code) errors.employee_code = 'Employee code is required.';
  else if (/\s/.test(code)) errors.employee_code = 'No spaces in the employee code.';
  if (!f.name.trim()) errors.name = 'Name is required.';
  if (f.leave_type && !LEAVE_TYPES.some((l) => l.value === f.leave_type)) errors.leave_type = 'Pick a valid leave type.';
  if (f.joining_date && !/^\d{4}-\d{2}-\d{2}$/.test(f.joining_date)) errors.joining_date = 'Enter a valid date.';

  const money = {};
  for (const { key, label } of MONEY_FIELDS) {
    money[key] = toMoney(f[key]);
    if (Number.isNaN(money[key])) errors[key] = `${label} must be a number.`;
    else if (money[key] !== null && money[key] < 0) errors[key] = `${label} cannot be negative.`;
  }

  const phone = normalizePhone(f.phone);
  if (phone && !isValidMobile(phone)) warnings.push(`Phone "${phone}" is not a valid 10-digit mobile — slips cannot be sent to it.`);
  if (!phone) warnings.push('No phone number — slips cannot be sent.');
  const uan = f.uan.replace(/\s/g, '');
  const uanProblem = uanIssue(uan);
  if (uanProblem === 'missing') warnings.push('No UAN — the slip will print "NA".');
  else if (uanProblem === 'invalid') warnings.push(`UAN "${uan}" is not 12 digits.`);
  // ESIC insurance number: 10 digits. IFSC: 11 characters — four letters, a
  // zero, then six letters or digits. Both advisory, like UAN.
  const esicNumber = f.esic_number.replace(/\s/g, '');
  if (esicNumber && !/^\d{10}$/.test(esicNumber)) warnings.push(`ESIC number "${esicNumber}" is not 10 digits.`);
  const ifsc = f.ifsc_code.replace(/\s/g, '').toUpperCase();
  if (ifsc && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc)) warnings.push(`IFSC "${ifsc}" does not look right — 11 characters: four letters, a zero, then six letters or digits.`);
  const cash = String(f.payment_type).toLowerCase() === 'cash';
  if (!cash && !f.account_number.trim()) warnings.push('No account number for a non-cash employee.');
  if (!cash && !ifsc) warnings.push('No IFSC for a non-cash employee — the slip will print "NA".');
  if (!f.leave_type && f.is_active) warnings.push('Leave type not set — payroll will refuse to run until every active employee has one.');
  if (!Object.keys(errors).length) {
    const mismatch = grossMismatch(money.gross_monthly, money.basic, money.da, money.residual_pay);
    if (mismatch !== null) warnings.push(`Gross monthly is ${inr(money.gross_monthly)} but Basic + DA + Residual pay = ${inr(mismatch)}.`);
    if (money.gross_monthly === null) warnings.push('No gross monthly salary — payroll cannot be computed for this employee.');
    if (money.esic > 0 && !esicNumber) warnings.push('ESIC is deducted but there is no ESIC number — the slip will print "NA".');
  }
  return { errors, warnings, money, phone, uan, esicNumber, ifsc };
}

export default function PayrollEmployees() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [form, setForm] = useState(null); // null = closed; otherwise the editable record
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [notice, setNotice] = useState('');
  const [attempted, setAttempted] = useState(false);
  // Status / delete confirmation: { action: 'deactivate'|'reactivate'|'delete',
  // row, history: null (loading) | { slips, attendance } | { error } }
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data, error: err } = await supabase.from('employees').select(SELECT).order('employee_code');
    if (err) {
      setError(/column|schema cache/i.test(err.message || '')
        ? `Could not load employees: the table is missing the new columns. Paste supabase/manual/2026-10-02_employees_designation_esic_number_ifsc.sql into the Supabase SQL editor, then refresh. (${err.message})`
        : `Could not load employees: ${err.message}`);
      setRows([]);
    }
    else setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const departments = useMemo(
    () => [...new Set(rows.map((r) => (r.department || '').trim()).filter(Boolean))].sort(),
    [rows],
  );
  const designations = useMemo(
    () => [...new Set(rows.map((r) => (r.designation || '').trim()).filter(Boolean))].sort(),
    [rows],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (!showInactive && r.is_active === false) return false;
      if (!q) return true;
      return [r.employee_code, r.name, r.designation, r.department, r.phone].some((v) => String(v || '').toLowerCase().includes(q));
    }).sort(byNameActiveFirst);
  }, [rows, query, showInactive]);

  const counts = useMemo(() => ({
    total: rows.length,
    active: rows.filter((r) => r.is_active !== false).length,
    inactive: rows.filter((r) => r.is_active === false).length,
    noSalary: rows.filter((r) => r.is_active !== false && (r.gross_monthly === null || r.gross_monthly === undefined)).length,
    wo: rows.filter((r) => r.is_active !== false && r.leave_type === 'WO').length,
    noLeave: rows.filter((r) => r.is_active !== false && !r.leave_type).length,
  }), [rows]);

  const openNew = () => { setForm(emptyForm()); setSaveError(''); setAttempted(false); setNotice(''); };
  const openEdit = (r) => { setForm(rowToForm(r)); setSaveError(''); setAttempted(false); setNotice(''); };
  const closeForm = () => { if (!saving) { setForm(null); setSaveError(''); } };
  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const validation = form ? validate(form) : null;

  const save = async () => {
    if (!form) return;
    setAttempted(true);
    const f = form;
    const { errors, money, phone, uan, esicNumber, ifsc } = validate(f);
    if (Object.keys(errors).length) return;
    setSaving(true);
    setSaveError('');
    const payload = {
      employee_code: f.employee_code.trim(),
      name: f.name.trim(),
      designation: f.designation.trim().replace(/\s+/g, ' ') || null,
      department: f.department.trim().replace(/\s+/g, ' ') || null,
      phone: phone || null,
      uan: uan || null,
      esic_number: esicNumber || null,
      payment_type: f.payment_type.trim() || null,
      account_number: f.account_number.trim() || null,
      ifsc_code: ifsc || null,
      ...Object.fromEntries(MONEY_FIELDS.map(({ key }) => [key, money[key]])),
      leave_type: f.leave_type || null,
      is_active: !!f.is_active,
      joining_date: f.joining_date || null,
    };
    const q = f.id
      ? supabase.from('employees').update(payload).eq('id', f.id).select(SELECT).single()
      : supabase.from('employees').insert(payload).select(SELECT).single();
    const { data, error: err } = await q;
    setSaving(false);
    if (err) {
      setSaveError(err.code === '23505'
        ? `Employee code "${payload.employee_code}" already exists — codes must be unique.`
        : `Save failed: ${err.message}`);
      return;
    }
    setRows((prev) => {
      const next = prev.filter((r) => r.id !== data.id).concat(data);
      next.sort(byNameActiveFirst);
      return next;
    });
    setForm(null);
    setNotice(`${data.name} (${data.employee_code}) ${f.id ? 'updated' : 'added'}${!data.is_active ? ' — inactive' : ''}.`);
  };

  // How much payroll history references this employee. Informational for
  // deactivation (shown as "kept"), the gate for deletion.
  const fetchHistory = async (id) => {
    const [slips, att] = await Promise.all([
      supabase.from('payslips').select('id', { count: 'exact', head: true }).eq('employee_id', id),
      supabase.from('payroll_attendance').select('id', { count: 'exact', head: true }).eq('employee_id', id),
    ]);
    if (slips.error || att.error) return { error: (slips.error || att.error).message };
    return { slips: slips.count || 0, attendance: att.count || 0 };
  };

  const openConfirm = async (action, row) => {
    setConfirm({ action, row, history: null });
    setConfirmError('');
    setNotice('');
    const history = await fetchHistory(row.id);
    setConfirm((c) => (c && c.row.id === row.id && c.action === action ? { ...c, history } : c));
  };
  const closeConfirm = () => { if (!confirmBusy) { setConfirm(null); setConfirmError(''); } };

  // Deactivate / reactivate: ONE column changes. Nothing else on the record
  // is touched (this deliberately does not go through the form's save path,
  // so unsaved edits in an open dialog are neither lost nor written).
  const runStatusChange = async () => {
    const { action, row } = confirm;
    const nextActive = action === 'reactivate';
    setConfirmBusy(true);
    setConfirmError('');
    const { data, error: err } = await supabase.from('employees')
      .update({ is_active: nextActive }).eq('id', row.id).select(SELECT).single();
    setConfirmBusy(false);
    if (err) { setConfirmError(`Could not ${action}: ${err.message}`); return; }
    setRows((prev) => prev.map((r) => (r.id === data.id ? data : r)));
    setForm((f) => (f && f.id === data.id ? { ...f, is_active: data.is_active } : f));
    setConfirm(null);
    setNotice(nextActive
      ? `${data.name} (${data.employee_code}) reactivated — included in payroll from the next run.`
      : `${data.name} (${data.employee_code}) deactivated — excluded from payroll from the next run. Past payslips and attendance are kept.`);
  };

  // Delete: only for a record with no payroll history. The database enforces
  // the same rule (FK RESTRICT), so a 23503 here means history appeared
  // between the check and the click — never a lost record.
  const runDelete = async () => {
    const { row, history } = confirm;
    if (!history || history.error || history.slips > 0 || history.attendance > 0) return;
    setConfirmBusy(true);
    setConfirmError('');
    const { data, error: err } = await supabase.from('employees').delete().eq('id', row.id).select('id');
    setConfirmBusy(false);
    if (err) {
      setConfirmError(err.code === '23503'
        ? 'The database refused: this employee now has payroll history. Deactivate them instead.'
        : `Could not delete: ${err.message}`);
      return;
    }
    if (!data || data.length !== 1) { setConfirmError('Delete did not affect exactly one record — nothing was changed. Reload and try again.'); return; }
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    setForm((f) => (f && f.id === row.id ? null : f));
    setConfirm(null);
    setNotice(`${row.name} (${row.employee_code}) deleted — the record had no payroll history.`);
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box' };
  const fieldErr = (k) => (attempted && validation?.errors[k]) || '';

  return (
    <div>
      {/* Toolbar */}
      <div style={{ ...card, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 360 }}>
          <Search size={14} aria-hidden="true" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-ink-muted)' }} />
          <input
            type="search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search code, name, designation, department, phone"
            aria-label="Search employees"
            style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px 7px 30px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: '0.82rem', color: NAVY, background: 'var(--color-surface)' }}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: 'var(--color-ink-secondary)', cursor: 'pointer' }}>
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          Show inactive ({counts.inactive})
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 14, alignItems: 'center', fontSize: '0.78rem', color: 'var(--color-ink-muted)' }}>
          <span><strong style={{ color: NAVY, ...num }}>{counts.active}</strong> active</span>
          <span><strong style={{ color: NAVY, ...num }}>{counts.wo}</strong> on weekly offs</span>
          {counts.noLeave > 0 && (
            <span style={{ color: 'var(--color-warning-strong)', fontWeight: 600 }}>
              <AlertTriangle size={13} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 4 }} />
              {counts.noLeave} active without a leave type
            </span>
          )}
          {counts.noSalary > 0 && (
            <span style={{ color: 'var(--color-warning-strong)', fontWeight: 600 }}>
              <AlertTriangle size={13} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 4 }} />
              {counts.noSalary} active without a salary
            </span>
          )}
          <Button size="sm" onClick={openNew}><Plus size={14} aria-hidden="true" style={{ marginRight: 4, verticalAlign: -2 }} />Add employee</Button>
        </div>
      </div>

      {notice && (
        <div style={{ ...card, marginBottom: 12, padding: '10px 14px', display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.82rem', color: 'var(--color-success-strong)', borderColor: 'var(--color-success-border)' }}>
          <CheckCircle2 size={15} aria-hidden="true" /> {notice}
        </div>
      )}
      {error && (
        <div style={{ ...card, marginBottom: 12, padding: '10px 14px', fontSize: '0.82rem', color: 'var(--color-danger-strong)', borderColor: 'var(--color-danger-border)' }}>{error}</div>
      )}

      {/* List */}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {loading ? (
          <div style={{ padding: 24, fontSize: '0.82rem', color: 'var(--color-ink-muted)', display: 'flex', gap: 8, alignItems: 'center' }}>
            <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} aria-hidden="true" /> Loading employees…
          </div>
        ) : visible.length === 0 ? (
          <div style={{ padding: 24, fontSize: '0.82rem', color: 'var(--color-ink-muted)' }}>
            {rows.length === 0 ? 'No employees yet. Add one, or run the master seed.' : 'No employees match.'}
          </div>
        ) : (
          <table className="data-table" style={{ width: '100%', fontSize: '0.8rem' }}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Designation</th>
                <th>Department</th>
                <th>Phone</th>
                <th>Payment</th>
                <th style={{ textAlign: 'right' }}>Gross</th>
                <th style={{ textAlign: 'right' }}>Basic</th>
                <th style={{ textAlign: 'right' }}>DA</th>
                <th style={{ textAlign: 'right' }}>Residual</th>
                <th style={{ textAlign: 'right' }}>PF</th>
                <th style={{ textAlign: 'right' }}>ESIC ded.</th>
                <th style={{ textAlign: 'right' }}>PT</th>
                <th>Leave</th>
                <th>Joined</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const inactive = r.is_active === false;
                const dim = inactive ? { color: 'var(--color-ink-muted)' } : null;
                return (
                  <tr key={r.id} style={dim} onDoubleClick={() => openEdit(r)}>
                    <td style={{ ...num, fontWeight: 600 }}>{r.employee_code}</td>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r.name}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{r.designation || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{r.department || '—'}</td>
                    <td style={num}>{r.phone || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{r.payment_type || '—'}</td>
                    <td style={numRight}>{r.gross_monthly === null ? <span style={{ color: 'var(--color-warning-strong)', fontWeight: 600 }}>—</span> : inr(r.gross_monthly)}</td>
                    <td style={numRight}>{inr(r.basic)}</td>
                    <td style={numRight}>{inr(r.da)}</td>
                    <td style={numRight}>{inr(r.residual_pay)}</td>
                    <td style={numRight}>{inr(r.pf)}</td>
                    <td style={numRight}>{inr(r.esic)}</td>
                    <td style={numRight}>{inr(r.professional_tax)}</td>
                    <td>
                      {r.leave_type === 'WO' ? <Badge tone="amber">WO</Badge>
                        : r.leave_type === 'L1' ? <span style={num}>L1</span>
                        : <span style={{ color: 'var(--color-warning-strong)', fontWeight: 600 }}>Not set</span>}
                    </td>
                    <td style={{ ...num, whiteSpace: 'nowrap' }}>{r.joining_date || '—'}</td>
                    <td>{inactive ? <Badge tone="grey">Inactive</Badge> : <Badge tone="green">Active</Badge>}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => openEdit(r)} aria-label={`Edit ${r.name}`}>
                        <Pencil size={13} aria-hidden="true" style={{ marginRight: 4, verticalAlign: -2 }} />Edit
                      </button>
                      {inactive ? (
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => openConfirm('reactivate', r)} aria-label={`Reactivate ${r.name}`}>
                          <UserCheck size={13} aria-hidden="true" style={{ marginRight: 4, verticalAlign: -2 }} />Reactivate
                        </button>
                      ) : (
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => openConfirm('deactivate', r)} aria-label={`Deactivate ${r.name}`}>
                          <UserX size={13} aria-hidden="true" style={{ marginRight: 4, verticalAlign: -2 }} />Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add / edit dialog */}
      {form && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: 16, overflowY: 'auto' }}
          onClick={closeForm}
        >
          <div
            role="dialog" aria-modal="true" aria-labelledby="emp-dialog-title"
            style={{ ...card, maxWidth: 760, width: '100%', background: '#ffffff', marginTop: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 14 }}>
              <h3 id="emp-dialog-title" style={{ fontSize: '0.95rem', fontWeight: 700, color: NAVY, margin: 0 }}>
                {form.id ? 'Edit employee' : 'Add employee'}
              </h3>
              {form.id && !form.is_active && <Badge tone="grey">Inactive</Badge>}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); save(); }} noValidate>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '4px 0 8px' }}>Identity</div>
              <div className="form-grid">
                <Field label="Employee code" htmlFor="emp-code" required error={fieldErr('employee_code')}>
                  <input id="emp-code" value={form.employee_code} onChange={set('employee_code')} style={inputStyle} autoFocus={!form.id} />
                </Field>
                <Field label="Name" htmlFor="emp-name" required error={fieldErr('name')}>
                  <input id="emp-name" value={form.name} onChange={set('name')} style={inputStyle} />
                </Field>
                <Field label="Designation" htmlFor="emp-desig" hint="Printed on the salary slip. Blank prints NA.">
                  <input id="emp-desig" list="emp-desig-list" value={form.designation} onChange={set('designation')} style={inputStyle} />
                  <datalist id="emp-desig-list">{designations.map((d) => <option key={d} value={d} />)}</datalist>
                </Field>
                <Field label="Department" htmlFor="emp-dept" hint="Pick an existing name to keep departments consistent.">
                  <input id="emp-dept" list="emp-dept-list" value={form.department} onChange={set('department')} style={inputStyle} />
                  <datalist id="emp-dept-list">{departments.map((d) => <option key={d} value={d} />)}</datalist>
                </Field>
                <Field label="Joining date" htmlFor="emp-join" hint="For the record only — CL / ML are not pro-rated." error={fieldErr('joining_date')}>
                  <input id="emp-join" type="date" value={form.joining_date} onChange={set('joining_date')} style={{ ...inputStyle, colorScheme: 'light' }} />
                </Field>
              </div>

              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '12px 0 8px' }}>Contact & payment</div>
              <div className="form-grid">
                <Field label="Phone" htmlFor="emp-phone">
                  <input id="emp-phone" inputMode="numeric" value={form.phone} onChange={set('phone')} style={inputStyle} />
                </Field>
                <Field label="UAN" htmlFor="emp-uan" hint="12 digits. Leave blank if none — the slip prints NA.">
                  <input id="emp-uan" inputMode="numeric" value={form.uan} onChange={set('uan')} style={inputStyle} />
                </Field>
                <Field label="ESIC number" htmlFor="emp-esic-no" hint="The employee's 10-digit insurance number — not the deduction amount. Blank prints NA.">
                  <input id="emp-esic-no" inputMode="numeric" value={form.esic_number} onChange={set('esic_number')} style={inputStyle} />
                </Field>
                <Field label="Payment type" htmlFor="emp-pay">
                  <select id="emp-pay" value={form.payment_type} onChange={set('payment_type')} style={inputStyle}>
                    {!PAYMENT_TYPES.includes(form.payment_type) && <option value={form.payment_type}>{form.payment_type || '—'}</option>}
                    {PAYMENT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Account number" htmlFor="emp-acct">
                  <input id="emp-acct" inputMode="numeric" value={form.account_number} onChange={set('account_number')} style={inputStyle} />
                </Field>
                <Field label="IFSC code" htmlFor="emp-ifsc" hint="Bank branch code, 11 characters. Blank prints NA.">
                  <input id="emp-ifsc" value={form.ifsc_code} onChange={set('ifsc_code')} style={{ ...inputStyle, textTransform: 'uppercase' }} maxLength={11} autoCapitalize="characters" spellCheck={false} />
                </Field>
              </div>

              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '12px 0 8px' }}>Salary components (monthly)</div>
              <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                {MONEY_FIELDS.map(({ key, label }) => (
                  <Field key={key} label={label} htmlFor={`emp-${key}`} error={fieldErr(key)}>
                    <input id={`emp-${key}`} inputMode="decimal" value={form[key]} onChange={set(key)} style={{ ...inputStyle, textAlign: 'right', ...num }} />
                  </Field>
                ))}
              </div>

              <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '12px 0 8px' }}>Attendance rules</div>
              <div className="form-grid">
                <Field label="Leave type" htmlFor="emp-leave" hint="Must be set for every active employee before payroll can run." error={fieldErr('leave_type')}>
                  <select id="emp-leave" value={form.leave_type} onChange={set('leave_type')} style={inputStyle}>
                    <option value="">Not set</option>
                    {LEAVE_TYPES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </Field>
                <Field label="Status" htmlFor="emp-active" hint="Inactive: excluded from payroll and from the missing-from-muster check; history kept.">
                  <label htmlFor="emp-active" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.84rem', color: NAVY, padding: '8px 0', cursor: 'pointer' }}>
                    <input id="emp-active" type="checkbox" checked={!!form.is_active} onChange={set('is_active')} />
                    Active employee
                  </label>
                </Field>
              </div>

              {validation && validation.warnings.length > 0 && (
                <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 6, background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-border)', fontSize: '0.78rem', color: 'var(--color-warning-strong)' }}>
                  <div style={{ fontWeight: 700, marginBottom: 4 }}><AlertTriangle size={13} aria-hidden="true" style={{ verticalAlign: -2, marginRight: 4 }} />Check before saving (does not block)</div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {validation.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              {saveError && (
                <div style={{ marginTop: 10, fontSize: '0.8rem', color: 'var(--color-danger-strong)', fontWeight: 600 }}>{saveError}</div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 16, flexWrap: 'wrap' }}>
                {form.id && (() => {
                  const row = rows.find((r) => r.id === form.id);
                  if (!row) return null;
                  return (
                    <div style={{ marginRight: 'auto', display: 'flex', gap: 6 }}>
                      <Button variant="secondary" size="sm" onClick={() => openConfirm(row.is_active === false ? 'reactivate' : 'deactivate', row)} disabled={saving}>
                        {row.is_active === false
                          ? <><UserCheck size={14} aria-hidden="true" style={{ marginRight: 4, verticalAlign: -2 }} />Reactivate</>
                          : <><UserX size={14} aria-hidden="true" style={{ marginRight: 4, verticalAlign: -2 }} />Deactivate</>}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openConfirm('delete', row)} disabled={saving} title="Only possible for a record with no payroll history">
                        <Trash2 size={14} aria-hidden="true" style={{ marginRight: 4, verticalAlign: -2 }} />Delete
                      </Button>
                    </div>
                  );
                })()}
                <Button variant="secondary" size="sm" onClick={closeForm} disabled={saving}>Cancel</Button>
                <Button type="submit" size="sm" disabled={saving}>
                  {saving ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite', marginRight: 6, verticalAlign: -2 }} aria-hidden="true" />Saving…</> : (form.id ? 'Save changes' : 'Add employee')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deactivate / reactivate / delete confirmation */}
      {confirm && (() => {
        const { action, row, history } = confirm;
        const h = history && !history.error ? history : null;
        const hasHistory = h ? (h.slips > 0 || h.attendance > 0) : null; // null = unknown
        const historyLine = history === null
          ? <span style={{ color: 'var(--color-ink-muted)' }}><Loader2 size={13} style={{ animation: 'spin 1s linear infinite', marginRight: 6, verticalAlign: -2 }} aria-hidden="true" />Checking payroll history…</span>
          : history.error
            ? <span style={{ color: 'var(--color-danger-strong)' }}>Could not check payroll history: {history.error}</span>
            : <span style={num}>{h.slips} payslip{h.slips === 1 ? '' : 's'}, {h.attendance} attendance row{h.attendance === 1 ? '' : 's'}</span>;
        const li = { margin: '3px 0' };
        const title = action === 'deactivate' ? 'Deactivate employee' : action === 'reactivate' ? 'Reactivate employee' : 'Delete employee';
        return (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: 16 }}
            onClick={closeConfirm}
          >
            <div role="alertdialog" aria-modal="true" aria-labelledby="emp-confirm-title" style={{ ...card, maxWidth: 480, width: '100%', background: '#ffffff' }} onClick={(e) => e.stopPropagation()}>
              <h3 id="emp-confirm-title" style={{ fontSize: '0.95rem', fontWeight: 700, color: NAVY, margin: '0 0 10px' }}>{title}</h3>
              <p style={{ fontSize: '0.84rem', color: NAVY, fontWeight: 600, margin: '0 0 10px' }}>{row.name} ({row.employee_code}){row.department ? ` — ${row.department}` : ''}</p>

              {action === 'deactivate' && (
                <ul style={{ fontSize: '0.82rem', color: 'var(--color-ink-secondary)', margin: '0 0 12px', paddingLeft: 18, lineHeight: 1.5 }}>
                  <li style={li}>Excluded from payroll calculations and from the leave-type check.</li>
                  <li style={li}>Not flagged as missing from the muster.</li>
                  <li style={li}>If a muster still lists them, that row is blocked with no slip until they are reactivated or the muster is corrected.</li>
                  <li style={li}>Past payslips and attendance rows stay exactly as stored — kept: {historyLine}.</li>
                  <li style={li}>Reversible at any time with Reactivate.</li>
                </ul>
              )}
              {action === 'reactivate' && (
                <ul style={{ fontSize: '0.82rem', color: 'var(--color-ink-secondary)', margin: '0 0 12px', paddingLeft: 18, lineHeight: 1.5 }}>
                  <li style={li}>Included in payroll calculations from the next run.</li>
                  <li style={li}>Payroll will refuse to run until they have a leave type{!row.leave_type ? ' — currently not set' : ''}; a salary is needed to compute pay{row.gross_monthly === null ? ' — currently not set' : ''}.</li>
                  <li style={li}>Flagged if missing from the muster.</li>
                  <li style={li}>Stored history: {historyLine}.</li>
                </ul>
              )}
              {action === 'delete' && (
                <div style={{ fontSize: '0.82rem', color: 'var(--color-ink-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
                  <p style={{ margin: '0 0 6px' }}>Payroll history: {historyLine}.</p>
                  {hasHistory === true && (
                    <p style={{ margin: 0, color: 'var(--color-danger-strong)', fontWeight: 600 }}>
                      Cannot delete — payslips or attendance rows reference this employee, and the database refuses the delete
                      as well. Deactivate them instead; every record is kept.
                    </p>
                  )}
                  {hasHistory === false && (
                    <p style={{ margin: 0 }}>
                      Nothing references this record, so it can be removed permanently. Use this only for an employee added by
                      mistake — a leaver should be deactivated, not deleted.
                    </p>
                  )}
                </div>
              )}

              {confirmError && (
                <p style={{ fontSize: '0.8rem', color: 'var(--color-danger-strong)', fontWeight: 600, margin: '0 0 10px' }}>{confirmError}</p>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="secondary" size="sm" onClick={closeConfirm} disabled={confirmBusy}>Cancel</Button>
                {action === 'delete' ? (
                  hasHistory === true ? (
                    <Button variant="primary" size="sm" disabled={confirmBusy} onClick={() => setConfirm({ action: 'deactivate', row, history })}>
                      <UserX size={14} aria-hidden="true" style={{ marginRight: 4, verticalAlign: -2 }} />Deactivate instead
                    </Button>
                  ) : (
                    <Button variant="danger" size="sm" onClick={runDelete} disabled={confirmBusy || hasHistory !== false}>
                      {confirmBusy ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite', marginRight: 6, verticalAlign: -2 }} aria-hidden="true" />Deleting…</> : 'Delete permanently'}
                    </Button>
                  )
                ) : (
                  <Button variant={action === 'deactivate' ? 'danger' : 'primary'} size="sm" onClick={runStatusChange} disabled={confirmBusy}>
                    {confirmBusy
                      ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite', marginRight: 6, verticalAlign: -2 }} aria-hidden="true" />Saving…</>
                      : action === 'deactivate' ? 'Deactivate' : 'Reactivate'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
